import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Box, Button, Typography } from '@mui/material';
import clsx from 'clsx';
import { AI_RESPONSE_TYPES, IAIAssistantType } from 'constants/AI';
import { useBreakpoints } from 'hooks';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan } from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { ReactComponent as DropdownArrow } from 'assets/icons/DropdownArrow.svg';
import { ReactComponent as LockIcon } from 'assets/icons/LockIcon.svg';

import { useBusinessPlanAIFieldUpdate } from 'hooks/useBusinessPlanAIFieldUpdate';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { UpgradePlanTooltip } from 'components/_basic/UpgradePlanTooltip';
import { AIAssistantContextMenu } from 'components/BusinessToolForm/components/AIAssistantContextMenu';
import { RequiredLabelTooltip } from 'components/RequiredLabelTooltip/RequiredLabelTooltip';

import { getValidatedMetadata } from 'helpers/metadataParser';

import { FORM_SECTION_CARD_VARIANT, IFormContainerProps } from './types';
import { AlertComponent } from '../AlertComponent';
import { TooltipComponent } from '../TooltipComponent';

import styles from './FormSectionCard.module.scss';

export const FormSectionCard: FC<IFormContainerProps> = ({
  children,
  title,
  subtitle,
  className,
  disabled,
  collapsible = false,
  requiredForAI = false,
  titleWrapperClassName,
  variant = FORM_SECTION_CARD_VARIANT.LARGE,
  aiFieldName,
  aiFieldType = AI_RESPONSE_TYPES.TEXT,
  aiOnlyUpdate = false,
  ...props
}) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const { handleApiRequest, loading, error, resetError } = useBusinessPlanAIFieldUpdate(aiFieldName, 20, aiFieldType);

  const { id: businessPlanId = '' } = useParams();
  const updateMutation = useUpdateBusinessPlan();

  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const isFieldAlreadyGenerated = businessPlan.metadata.aiGeneratedFields
    ? businessPlan.metadata.aiGeneratedFields.includes(
        Array.isArray(aiFieldName) ? aiFieldName.join(',') : (aiFieldName as string),
      )
    : false;

  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleClick = () => {
    setIsCollapsed(!isCollapsed);
  };

  const errorRef = useRef(error);

  // Synchronize the ref with the error state
  useEffect(() => {
    errorRef.current = error;
  }, [error]);

  const handleApiRequestClick = async (aiAssistantMenuType: IAIAssistantType) => {
    try {
      await handleApiRequest(aiAssistantMenuType);

      if (!errorRef.current) {
        const params = {
          id: businessPlanId,
          body: {
            ...businessPlan,
            metadata: JSON.stringify({
              ...getValidatedMetadata(businessPlan.metadata),
              aiGeneratedFields: [...businessPlan.metadata.aiGeneratedFields, aiFieldName],
            }),
          },
        };
        updateMutation.mutateAsync(params);
      }
    } catch (error) {
      console.error('Error in handleApiRequestClick:', error);
    }
  };

  return (
    <Box
      {...props}
      className={clsx(className, styles.container, {
        [styles.largeContainer]: variant === FORM_SECTION_CARD_VARIANT.LARGE,
      })}
      data-ai-field-name={aiFieldName}
    >
      {title && (
        <Box
          className={clsx(styles.titleContainer, titleWrapperClassName)}
          sx={{ marginBottom: collapsible && isCollapsed ? 0 : subtitle ? 0.5 : 3 }}
        >
          <Typography variant="h6" className={clsx(styles.title, { [styles.disabled]: disabled })}>
            <RequiredLabelTooltip text={title} requiredForAI={requiredForAI} tooltipText={t('requiredForAI')} />
          </Typography>
          {disabled && <LockIcon />}
          {aiFieldName && !disabled && (
            <TooltipComponent
              title={<UpgradePlanTooltip />}
              isTooltipHidden={isStandardOrLightTariff || !isFieldAlreadyGenerated}
              trigger={isMobile ? 'click' : 'hover'}
              leaveDelay={isMobile ? 6500 : 200}
              isTitleIsNode
              popperOffset={[20, 8]}
            >
              <AIAssistantContextMenu
                fieldName={aiFieldName}
                fieldType={aiFieldType}
                isLoading={loading}
                onApiRequest={handleApiRequestClick}
                onlyUpdate={aiOnlyUpdate}
                isAlreadyGenerated={!isStandardOrLightTariff && isFieldAlreadyGenerated}
              />
            </TooltipComponent>
          )}
          {collapsible && (
            <Button className={styles.collapseButton} onClick={handleClick}>
              <DropdownArrow className={clsx(styles.collapseIcon, { [styles.collapsed]: isCollapsed })} />
            </Button>
          )}
        </Box>
      )}
      {subtitle && (
        <Typography variant="subtitle2" sx={{ marginBottom: 3 }} className={clsx({ [styles.disabled]: disabled })}>
          {subtitle}
        </Typography>
      )}
      {!collapsible ? children : isCollapsed ? null : children}

      {error && error.message !== 'Something went wrong. Please try again later' && (
        <AlertComponent
          severity="error"
          title={error.title}
          message={error.message}
          sx={{ marginTop: 2 }}
          onClose={resetError}
        />
      )}
    </Box>
  );
};
